import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import DotsHorizontalSvg from "@redotech/redo-icons/dots-horizontal.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Flex } from "@redotech/redo-web/flex";
import { formatShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { Fragment, memo, useCallback, useState } from "react";
import { ConversationActionsDropdown } from "../actions/conversation-actions-dropdown";
import { useConversationActionToolbar } from "../hooks/use-conversation-action-toolbar";
import { MergeTicketModal } from "./merge-ticket/merge-ticket-modal";

export interface ConversationActionToolbarProps {
  conversationId: ConversationId;
  conversation: ConversationListItemResponse;
  disabled?: boolean;
}

export const ConversationActionToolbar = memo(
  function ConversationActionToolbar({
    conversationId,
    conversation,
    disabled = false,
  }: ConversationActionToolbarProps) {
    const {
      primaryAction,
      conversation: ctx,
      handlePrimaryAction,
    } = useConversationActionToolbar(conversationId, conversation);

    const [menuOpen, setMenuOpen] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);

    const handleClose = useCallback(() => setMenuOpen(false), []);
    const handleOpenMergeModal = useCallback(() => setMergeOpen(true), []);
    const handleCloseMergeModal = useCallback(() => setMergeOpen(false), []);

    const handleMerge = useCallback(() => {
      // TODO: call merge API when backend is ready
      setMergeOpen(false);
    }, []);

    const primaryEnabled = primaryAction.isEnabled?.(ctx) ?? true;
    const primaryVisible = primaryAction.isVisible?.(ctx) ?? true;

    return (
      <Fragment>
        <Flex align="center" gap="sm">
          <DropdownMenu onOpenChange={setMenuOpen} open={menuOpen}>
            <DropdownMenuTrigger asChild>
              <RedoButton
                disabled={disabled}
                hierarchy="secondary"
                IconLeading={DotsHorizontalSvg}
                size="sm"
              />
            </DropdownMenuTrigger>
            <ConversationActionsDropdown
              conversation={conversation}
              conversationId={conversationId}
              isOpen={menuOpen}
              onClose={handleClose}
              onMergeTicket={handleOpenMergeModal}
            />
          </DropdownMenu>
          {primaryVisible && (
            <Tooltip
              arrow
              title={
                primaryAction.shortcut
                  ? formatShortcut(primaryAction.shortcut)
                  : undefined
              }
            >
              <span>
                <RedoButton
                  disabled={disabled || !primaryEnabled}
                  hierarchy="primary"
                  IconLeading={primaryAction.Icon}
                  onClick={handlePrimaryAction}
                  size="sm"
                  text={primaryAction.title}
                />
              </span>
            </Tooltip>
          )}
        </Flex>
        <MergeTicketModal
          currentConversation={conversation}
          onClose={handleCloseMergeModal}
          onMerge={handleMerge}
          open={mergeOpen}
        />
      </Fragment>
    );
  },
);
